import type {
  AmmSpecificQuote,
  AccountSnapshot,
  ActivitySnapshot,
  BrokerSnapshot,
  IndexedLoanPage,
  LoanLiveSnapshot,
  NftMetadata,
  ProtocolSnapshot,
} from "@stonkland/protocol/api-types";
import { publicEnvironment } from "@stonkland/protocol/public-env";

type ApiErrorBody = {
  error?: string;
  code?: string;
};

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
    this.code = code;
  }
}

async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  const base = (publicEnvironment("VITE_API_BASE_URL") || "").replace(/\/+$/, "");
  const response = await fetch(`${base}${path}`, { cache: "no-store", signal });
  const body = (await response.json()) as T & ApiErrorBody;
  if (!response.ok) {
    throw new ApiRequestError(
      body.error || `Request failed with status ${response.status}.`,
      response.status,
      body.code,
    );
  }
  return body;
}

export const protocolApi = {
  protocol: (signal?: AbortSignal) =>
    get<ProtocolSnapshot>("/api/protocol", signal),
  account: (address: string, signal?: AbortSignal) =>
    get<AccountSnapshot>(`/api/account/${address}`, signal),
  broker: (tokenId: bigint, signal?: AbortSignal) =>
    get<BrokerSnapshot>(`/api/brokers/${tokenId}`, signal),
  activity: (limit = 20, signal?: AbortSignal) =>
    get<ActivitySnapshot>(`/api/activity?limit=${limit}`, signal),
  loans: (limit = 20, signal?: AbortSignal) =>
    get<IndexedLoanPage>(`/api/indexed/loans?limit=${limit}`, signal),
  liveLoans: (ids: string[], signal?: AbortSignal) =>
    get<LoanLiveSnapshot[]>(
      `/api/loans/live?ids=${encodeURIComponent(ids.join(","))}`,
      signal,
    ),
  specificBuyQuote: (tokenId: bigint, signal?: AbortSignal) =>
    get<AmmSpecificQuote>(`/api/amm/quote-specific/${tokenId}`, signal),
  metadata: (tokenId: bigint, signal?: AbortSignal) =>
    get<NftMetadata>(`/api/metadata/${tokenId}`, signal),
};
